// ./src/components/features/DisplayLayoutsTable/DisplayLayoutsTable.tsx
import React, { useMemo } from "react";
import { Stack, Text, Image } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";

import { DisplayLayout } from "@/utils/api";

export interface DisplayLayoutsTableProps {
  /** Array of display layouts to display */
  displayLayouts: DisplayLayout[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditLayout?: (layout: DisplayLayout) => void;

  /** Callback when duplicate action is clicked */
  onDuplicateLayout?: (layout: DisplayLayout) => void;

  /** Callback when remove action is clicked */
  onRemoveLayout?: (layout: DisplayLayout) => void;

  /** Callback when view action is clicked */
  onViewLayout?: (layout: DisplayLayout) => void;

  /** ID of layout currently being removed */
  removingLayoutId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

const customLayoutPreviewUrl =
  "https://venuboardsassets-f3btcbbda5bnbmg5.z02.azurefd.net/previews/display-layouts/custom-layout-512px.png";

export const DisplayLayoutsTable: React.FC<DisplayLayoutsTableProps> = ({
  displayLayouts,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditLayout,
  onDuplicateLayout,
  onRemoveLayout,
  removingLayoutId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<DisplayLayout>[] = useMemo(
    () => [
      {
        key: "previewUrl",
        label: "",
        width: 64,
        minWidth: 64,
        render: (value, record) => (
          <Image
            src={value || customLayoutPreviewUrl}
            alt={`${record.name} preview`}
            style={{ width: "64px", height: "auto" }}
          />
        ),
      },
      {
        key: "name",
        label: "Layout Name",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record) => (
          <Stack gap={0}>
            <Text fw={500}>{value}</Text>
            {!!record.description && (
              <Text c="dimmed" size="sm" fw={500}>
                {record.description}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 140,
        minWidth: 140,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 230,
        minWidth: 230,
        align: "left",
        render: (_, record: any) => (
          <RecordActions
            context={record}
            onEditClick={
              record.rights?.includes("edit") ? onEditLayout : undefined
            }
            onRemoveClick={
              record.rights?.includes("remove") ? onRemoveLayout : undefined
            }
            onDuplicateClick={
              record.rights?.includes("duplicate")
                ? onDuplicateLayout
                : undefined
            }
            removeInProgress={removingLayoutId === record.id}
          />
        ),
      },
    ],
    [onEditLayout, onDuplicateLayout, onRemoveLayout, removingLayoutId]
  );

  return (
    <AppTable
      data={displayLayouts}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(layout) => layout?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
